import React from 'react';
import { observer } from 'mobx-react';
import { observable, when, computed } from 'mobx';
import { AppUI } from '@seedlang/state';
import { AuthenticateStore } from '@seedlang/stores';
import Form from 'components/form_factory/form';
import FormFactory from 'components/form_factory/form_factory';
import styled from '@emotion/styled';
import Text from 'components/text';
import OutlineButton from 'components/button/outline_button';
import { TimeZones, Constants, Theme } from '@seedlang/constants';
import Alert from 'components/alert';
import { SettingsBillingWrapper, SettingsStreakRepairWrapper } from '@seedlang/hoc';
import { isPresent, isBlank } from '@seedlang/utils';
import SettingsUserLanguage from './settings_user_language';
import DeleteAccountModal from 'components/modal/delete_account_modal';
import Spinner from 'components/spinner';
import PageTitle from 'components/common/page_title';
import Stack, { gaps } from 'components/layout/stack';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  textarea {
    height: 80px!important;
  }
`;

const Fields = styled.div`

`;

const ConfirmationLink = styled.button`
  background: none;
  border: none;
  color: ${Theme.blue};
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  text-decoration: underline;
  padding: 0;
  margin: 0 0 20px;
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

@observer
class SettingsUserProfileSeedlang extends React.Component {
  @observable formLoaded = false;
  @observable showConfirmDeleteModal = false;
  @observable previousEmail = null;
  @observable emailHasChanged = false;
  @observable emailError = null;
  @observable sendingConfirmation = false;
  @observable confirmationSentTo = null;
  @observable confirmationError = null;

  constructor(props) {
    super(props);
    if (!AppUI.levelStore.hasIndexData) {
      AppUI.levelStore.getIndex();
    }
    when(() => {
      if (AppUI.userIsLoaded) {
        this.formLoaded = true;
        this.previousEmail = AppUI.user.email;
        this.form = new FormFactory(AppUI.authUserStore, {
          ...this.props,
          onAfterUpdate: this.onAfterUpdate,
        });
      }
    });
  }

  onEmailChange = (field, value) => {
    this.form.onChange(field, value);
    this.emailError = null;
    this.confirmationSentTo = null;
    this.confirmationError = null;
  };

  sendConfirmationEmail = async () => {
    if (this.sendingConfirmation) return;
    const email = AppUI.user.email;
    this.sendingConfirmation = true;
    this.confirmationSentTo = null;
    this.confirmationError = null;
    try {
      const response = await AuthenticateStore.sendConfirmationInstructions();
      this.confirmationSentTo = response.email || email;
    } catch (error) {
      this.confirmationError = error.message || 'We could not send the confirmation email. Please try again.';
    } finally {
      this.sendingConfirmation = false;
    }
  };

  onAfterUpdate = async response => {
    if (response && response.failure) {
      this.emailError = response.message;
      if (this.form.store.formCache.email !== this.previousEmail) {
        this.form.store.setFormCacheValue('email', this.previousEmail);
        this.form.store.pristineFormCache.email = this.previousEmail;
      }
      return;
    }

    this.emailError = null;
    await AppUI.loadUserPromise();
    this.emailHasChanged =
      this.form.store.formCache.email &&
      this.previousEmail &&
      this.form.store.formCache.email !== this.previousEmail;

    if (this.emailHasChanged) {
      this.confirmationSentTo = AppUI.user.email;
      this.previousEmail = AppUI.user.email;
    }

    if (this.props.onAfterUpdate) {
      this.props.onAfterUpdate(response);
    }
  };

  @computed get streakExtendGoal() { // see user.rb
    if (isBlank(AppUI.authUserStore?.formCache?.dailyXpGoal)) { return AppUI.user?.streakExtendGoal; }
    return Math.max(100, AppUI.authUserStore?.formCache?.dailyXpGoal * 2);
  }

  fields() {
    return (
      <Fields>
        {this.confirmationSentTo &&
          <Alert background={Theme.green} role='status'>
            {this.emailHasChanged ? 'Please confirm your new email address. We sent a confirmation email to ' : 'A confirmation email has been sent to '}{this.confirmationSentTo}.
          </Alert>
        }
        {this.confirmationError &&
          <Alert background={Theme.red} role='alert'>{this.confirmationError}</Alert>
        }
        {
          AppUI.authUserStore.dirtyForm &&
            <Alert
              textAlign='center'
            >
              You have unsaved changes.
            </Alert>
        }
        {
          this.emailError &&
            <Alert
              textAlign='center'
              error
            >
              That email address was already taken.
            </Alert>
        }
        {
          isPresent(AppUI.user?.email) && !AppUI.user.email.endsWith('@apple.com') && ! AppUI.user.email.endsWith('privaterelay.appleid.com') &&
            this.form.input({
              type: 'email',
              required: true,
              margin: AppUI.userIsSignedInAndNotGuest && !AppUI.user.confirmed ? '0 0 4px 0' : '0 0 20px 0',
              name: 'email',
              label: 'Your Email',
              onChange: this.onEmailChange,
            })
        }
        {
          !this.confirmationSentTo && AppUI.userIsSignedInAndNotGuest && !AppUI.user.confirmed && isPresent(AppUI.user.email) &&
          !AppUI.user.email.endsWith('@apple.com') && !AppUI.user.email.endsWith('privaterelay.appleid.com') &&
            <ConfirmationLink
              type='button'
              title={this.form.store.formCache.email !== AppUI.user.email ? 'Save your email address before sending a confirmation email.' : undefined}
              disabled={this.sendingConfirmation || this.form.store.formCache.email !== AppUI.user.email}
              onClick={this.sendConfirmationEmail}
            >
              {this.sendingConfirmation ? 'Sending…' : 'Send confirmation email'}
            </ConfirmationLink>
        }
        { this.form.input({ margin: '0 0 20px 0', name: 'name', label: 'Your name', maxLength: Constants.MAX_NAME_LENGTH }) }
        { this.form.input({ margin: '0 0 20px 0', name: 'location', label: 'Your location' }) }
        { this.form.textarea({ margin: '0 0 20px 0', name: 'introduction', label: 'Your introduction' }) }
        { this.form.select({margin: '0 0 20px 0', name: 'timeZone', label: 'Your Time Zone', options: TimeZones})}
        <SettingsUserLanguage form={this.form} />
        {
          this.form.select({
            margin: '0 0 20px 0',
            name: 'dailyXpGoal',
            label: 'Your Daily XP Goal',
            options: this.props.dailyXpGoalOptions,
            includeBlank: false,
          })
        }
        {
          this.form.toggle({
            margin: '0 0 20px 0',
            name: 'enableStreakExtension',
            label: `Get an extra Streak day at ${this.streakExtendGoal} XP`,
          })
        }
        {
          this.form.toggle({
            margin: '0 0 20px 0',
            name: 'blockUserTracking',
            label: 'Block tracking of user activity',
          })
        }
        {
          this.form.toggle({
            margin: '0 0 20px 0',
            name: 'hideOnLeaderboard',
            label: 'Don\'t Add Me to Leagues',
          })
        }
        {
          this.form.toggle({
            margin: '0 0 20px 0',
            name: 'hideTriviaRecord',
            label: 'Hide my trivia win/loss record',
          })
        }
      </Fields>
    );
  }

  render() {
    return (
      <Wrapper>
        <PageTitle>User Settings</PageTitle>
        {AppUI.authUserStore.hasOutstandingRequests && <Spinner />}
        {
          this.showConfirmDeleteModal &&
            <DeleteAccountModal
              onClose={() => this.showConfirmDeleteModal = false}
              activeStripeSubscriptionExists={this.props.activeStripeSubscriptionExists}
              otherSitesWarning={this.props.otherSitesWarning}
            />
        }
        <Text heading='3'>
          User Settings
        </Text>
        {
          this.formLoaded &&
            <Form
              form={this.form}
              fields={this.fields()}
              {...this.props}
            />
        }
        <Text heading='3'>
          Delete Account
        </Text>
        <Stack gap='sm'>
          <OutlineButton
            selectedBackground='#CCC'
            onClick={() => this.showConfirmDeleteModal = true}
          >
            Delete Account
          </OutlineButton>
        </Stack>
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(SettingsStreakRepairWrapper(SettingsUserProfileSeedlang));
